import React from 'react';
import { isDesktopApp, isDesktopOffline } from '@/api/desktopSync';
import useDesktopUpdateOwner from '@/hooks/useDesktopUpdateOwner';

/**
 * DesktopUpdateBar — occupies the same under-navigation status slot as the
 * offline bar. A completed update remains visible while offline because it can
 * be installed from the validated local queue without the release service.
 */
export default function DesktopUpdateBar() {
  const { status, phase, claim, beginInstall, refresh } = useDesktopUpdateOwner();
  const offline = isDesktopOffline();
  const startingInstall = !!claim;
  if (!isDesktopApp()) return null;
  if (claim?.phase === 'unknown' || phase === 'unavailable') return <div data-testid="desktop-update-bar" role="status" className="px-3 py-2 bg-amber-600 text-white text-xs">
    {claim?.phase === 'unknown' ? 'The update outcome is unconfirmed. Do not start another installation. Follow desktop recovery guidance or contact support.'
      : 'Update status is unavailable. You can continue using the app.'}
    <button className="ml-3 underline" onClick={refresh}>Refresh update status</button>
  </div>;
  if (claim?.phase === 'handoff') return <div data-testid="desktop-update-bar" role="status" className="px-3 py-2 bg-emerald-600 text-white text-xs">
    Starting… The installer was handed control. Installation completion is not yet confirmed.
  </div>;
  if (status.state === 'idle') return null;

  if (status.state === 'downloaded' || status.state === 'retry-ready') {
    const retry = status.state === 'retry-ready';
    return (
      <div
        className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium flex-shrink-0 flex items-center justify-between gap-3"
        data-testid="desktop-update-bar"
      >
        <span>
          {retry
            ? 'The previous update attempt did not finish. Save your work, then retry.'
            : `Update ${status.version || ''} ready${offline ? ' offline' : ''}. Save your work, then select Restart to install.`.replace('Update  ready', 'Update ready')}
        </span>
        <button
          type="button"
          className="shrink-0 rounded-md border border-white/70 bg-white/10 px-2.5 py-1 font-semibold hover:bg-white/20 disabled:cursor-wait disabled:opacity-70"
          disabled={startingInstall || phase !== 'ready'}
          onClick={beginInstall}
        >
          {startingInstall
            ? 'Starting…'
            : (retry ? 'Retry update' : 'Restart to install')}
        </button>
      </div>
    );
  }

  if (status.state === 'blocked') {
    return (
      <div
        className="px-3 py-2 bg-amber-700 text-white text-xs font-medium flex-shrink-0"
        data-testid="desktop-update-bar"
      >
        The update could not be installed automatically. You may continue using the current version. Please contact an administrator.
      </div>
    );
  }

  if (status.state === 'interrupted') {
    return (
      <div
        className="px-3 py-2 bg-amber-600 text-white text-xs font-medium flex-shrink-0"
        data-testid="desktop-update-bar"
      >
        The update could not start. You may continue using this version and check update status again later.
      </div>
    );
  }

  if (status.state !== 'downloading') return null;

  const percent = Number.isFinite(status.percent)
    ? Math.round(Math.min(100, Math.max(0, status.percent)))
    : 0;

  return (
    <div
      className="px-3 py-2 bg-blue-600 text-white text-xs font-medium flex-shrink-0"
      data-testid="desktop-update-bar"
    >
      <div className="flex items-center justify-between gap-3">
        <span>
          {status.version
            ? `Desktop update ${status.version} is downloading. Please keep the app open.`
            : 'A desktop update is downloading. Please keep the app open.'}
        </span>
        <span className="tabular-nums">{percent}%</span>
      </div>
      <div
        className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-blue-300"
        role="progressbar"
        aria-label="Desktop update download progress"
        aria-valuemin="0"
        aria-valuemax="100"
        aria-valuenow={percent}
      >
        <div
          className="h-full rounded-full bg-white transition-[width] duration-300"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}
